import isDate from 'lodash/isDate';
import isString from 'lodash/isString';
import mapValues from 'lodash/mapValues';

export type DateWithTimezoneProps = {
  date: Date;
  tz?: string;
};

export type DateFormatterInput = Date | DateWithTimezoneProps;

export const isValidDate = (value: unknown): value is Date =>
  isDate(value) && !Number.isNaN(value.valueOf());

export const isDateWithTimezoneProps = (value: unknown): value is DateWithTimezoneProps =>
  !!value &&
  typeof value === 'object' &&
  isValidDate((value as DateWithTimezoneProps).date) &&
  (isString((value as DateWithTimezoneProps).tz) ||
    (value as DateWithTimezoneProps).tz === undefined);

export type DateFormat =
  | 'formatHourOnly'
  | 'formatHourOnlyUTC'
  | 'formatDayOnly'
  | 'formatWeekdayOnly'
  | 'formatMonthOnly'
  | 'formatMonthAndDay'
  | 'formatYearOnly'
  | 'formatShortTime'
  | 'formatShortTime24Hour'
  | 'formatShortDate'
  | 'formatShortDateUTC'
  | 'formatShortDateAndWeekday'
  | 'formatShortDateAndTime'
  | 'formatShortDateAndTimeUTC'
  | 'formatShortDateAndMediumTime'
  | 'formatShortDateAndMediumTimeUTC';
export type DateFormatters = Record<DateFormat, (date?: DateFormatterInput) => string>;

const buildIntlFormatter = (
  lng: string | string[] | undefined,
  options: Intl.DateTimeFormatOptions,
) => {
  const timezoneFormatterMap = new Map<string, Intl.DateTimeFormat>();
  return (value?: DateFormatterInput) => {
    if (isDateWithTimezoneProps(value) && value.tz) {
      const existingFormatter = timezoneFormatterMap.get(value.tz);
      if (existingFormatter) {
        return existingFormatter.format(value.date);
      }
      const formatter = new Intl.DateTimeFormat(lng, { ...options, timeZone: value.tz });
      timezoneFormatterMap.set(value.tz, formatter);
      return formatter.format(value.date);
    }

    const date = isDateWithTimezoneProps(value) ? value.date : value;
    const formatterKey = 'local';
    const existingFormatter = timezoneFormatterMap.get(formatterKey);
    if (existingFormatter) {
      return existingFormatter.format(date);
    }
    const formatter = new Intl.DateTimeFormat(lng, options);
    timezoneFormatterMap.set(formatterKey, formatter);
    return formatter.format(date);
  };
};

const intlFormatOptions: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  formatHourOnly: { hour: 'numeric' },
  formatHourOnlyUTC: { hour: 'numeric', timeZone: 'UTC' },
  formatDayOnly: { day: 'numeric' },
  formatWeekdayOnly: { weekday: 'long' },
  formatMonthOnly: { month: 'long' },
  formatMonthAndDay: { month: 'long', day: 'numeric' },
  formatYearOnly: { year: 'numeric' },
  formatShortTime: { hour: 'numeric', minute: '2-digit' },
  formatShortTime24Hour: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  formatShortDate: { year: 'numeric', month: 'numeric', day: 'numeric' },
  formatShortDateUTC: { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC' },
  formatShortDateAndWeekday: { year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'long' },
  formatShortDateAndTime: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  },
  formatShortDateAndTimeUTC: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: 'UTC',
  },
  formatShortDateAndMediumTime: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  },
  formatShortDateAndMediumTimeUTC: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
  },
};

export const getDateFormatters = (lng: string | string[] | undefined): DateFormatters =>
  mapValues(intlFormatOptions, (options) => buildIntlFormatter(lng, options));
